import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { loadAllDataFromFirestore, syncAllDataToFirestore } from '../firebase/firestoreUtils';
import { loadDataFromFirestore } from '../utils/storageUtils';

const AuthContext = createContext();
const googleProvider = new GoogleAuthProvider();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  async function signup(email, password) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // After signup, sync any existing localStorage data to Firestore
    setSyncing(true);
    try {
      const syncResult = await syncAllDataToFirestore(result.user.uid);
      console.log('Data synced to Firestore:', syncResult);
    } catch (error) {
      console.error('Error syncing data after signup:', error);
    } finally {
      setSyncing(false);
    }
    
    return result;
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function loginWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    const additionalInfo = getAdditionalUserInfo(result);

    if (additionalInfo?.isNewUser) {
      // New Google user — sync any existing localStorage data to Firestore
      setSyncing(true);
      try {
        const syncResult = await syncAllDataToFirestore(result.user.uid);
        console.log('Data synced to Firestore for new Google user:', syncResult);
      } catch (error) {
        console.error('Error syncing data after Google signup:', error);
      } finally {
        setSyncing(false);
      }
    }

    return result;
  }

  async function logout() {
    // Clear localStorage on logout so next user doesn't see this user's data
    localStorage.removeItem('ndis_clients');
    localStorage.removeItem('ndis_shifts');
    localStorage.removeItem('ndis_invoices');
    localStorage.removeItem('ndis_settings');
    localStorage.removeItem('ndis_service_agreements');
    
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // User logged in
        setSyncing(true);
        try {
          // Check if this is a different user than last session
          const lastUserId = localStorage.getItem('ndis_last_user_id');
          const isNewUser = lastUserId && lastUserId !== user.uid;
          
          if (isNewUser) {
            // Different user — clear old data to prevent bleed
            console.log('User changed, clearing old localStorage data');
            localStorage.removeItem('ndis_clients');
            localStorage.removeItem('ndis_shifts');
            localStorage.removeItem('ndis_invoices');
            localStorage.removeItem('ndis_service_agreements');
            localStorage.removeItem('recurringSchedules');
            localStorage.removeItem('ndis_settings');
            localStorage.removeItem('ndis_shift_notes');
          }
          localStorage.setItem('ndis_last_user_id', user.uid);

          // First, check if user has data in Firestore
          const firestoreData = await loadAllDataFromFirestore(user.uid);
          
          // Check if Firestore has any data
          const hasFirestoreData = 
            (firestoreData.clients && firestoreData.clients.length > 0) ||
            (firestoreData.shifts && firestoreData.shifts.length > 0) ||
            (firestoreData.invoices && firestoreData.invoices.length > 0) ||
            (firestoreData.recurringSchedules && firestoreData.recurringSchedules.length > 0);
          
          // Check if localStorage has any data
          const hasLocalData = 
            localStorage.getItem('ndis_clients') ||
            localStorage.getItem('ndis_shifts') ||
            localStorage.getItem('ndis_invoices');
          
          if (hasFirestoreData) {
            // Firestore has data - load it (merge, don't wipe)
            console.log('Loading data from Firestore');
            loadDataFromFirestore(firestoreData);
          } else if (hasLocalData) {
            // Firestore is empty but localStorage has data - sync it up
            console.log('Syncing localStorage data to Firestore');
            await syncAllDataToFirestore(user.uid);
          } else {
            // Both empty - fresh start
            console.log('No data found - fresh start');
          }
        } catch (error) {
          console.error('Error loading data:', error);
        } finally {
          setSyncing(false);
        }
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    resetPassword,
    loginWithGoogle,
    syncing
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      {syncing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#6366f1',
          color: 'white',
          padding: '0.5rem',
          textAlign: 'center',
          zIndex: 9999
        }}>
          Syncing data...
        </div>
      )}
    </AuthContext.Provider>
  );
}
